import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ScrollText,
  Handshake,
  Send,
} from "lucide-react";
import { toast } from "react-toastify";

import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import PendingInboxPanel from "./PendingInboxPanel";
import FeedPanel from "../feed/FeedPanel";
import AddNewDeal from "./add-deal";
import SaleTurnInSourcePicker from "./SaleTurnInSourcePicker";
import {
  getAlldealforAdmin,
  getTaskTemplate,
  saveitemwithid,
  updateItem,
  uploadImagesToFirebase,
} from "../../utils/firebasefunctions";
import {
  newmessagesuccess,
  notifyAgentOnFileComplete,
  notifyApprovers,
} from "../../utils/helper";
import PageHeader from "../PageHeader";

const COLUMN_IDS = [
  "Prospect",
  "On Hold",
  "CMAs",
  "Listing Appointment",
  "Pre-Market",
  "Coming Soon",
  "Active",
  "Under Contract",
  "Pending",
  "Closed",
  "Cancelled",
  "Expired",
];

function SupervisorDashboard() {
  const [{ user, users, deals, steps }, dispatch] = useStateValue();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tasks, setTasks] = useState({});
  // Sale Turn-In must open the source-picker first so the user picks the
  // originating listing/buyer request and the deal modal pre-fills.
  // Matches the agent flow in hero.jsx.
  const [turnInPickerOpen, setTurnInPickerOpen] = useState(false);
  const [turnInSeed, setTurnInSeed] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getTaskTemplate();
        setTasks(data || {});
      } catch (err) {
        console.error("Failed to load task templates:", err);
      }
    })();
  }, []);

  // Set up Google OAuth for Sync Contacts.
  useEffect(() => {
    if (!window.google?.accounts?.oauth2) return;
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id:
        "627842934381-7bra6ha78jub716ceu9hq433chsh69dn.apps.googleusercontent.com",
      scope: "https://www.googleapis.com/auth/contacts.readonly",
      callback: async (response) => {
        if (!response.access_token) return;
        try {
          const res = await fetch(
            "https://us-central1-team-edelstein.cloudfunctions.net/api/getcontacts",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ token: response.access_token }),
            },
          );
          if (res.status === 200) {
            const dataresponse = await res.json();
            localStorage.setItem(
              "sync-ontact",
              JSON.stringify({ contacts: dataresponse.data }),
            );
            toast.success("Contacts Synced Successfully!");
          } else {
            toast.error("Syncing Failed");
          }
        } catch (err) {
          toast.error("Authentication Error");
          console.error("Error:", err);
        }
      },
    });
    window.loginWithGoogle = () => client.requestAccessToken();
  }, []);

  async function refreshDeals() {
    try {
      const response = await getAlldealforAdmin("deals");
      dispatch({ type: actionType.STORE_DEALS, deals: response });
    } catch (err) {
      console.error("Failed to refresh deals:", err);
    }
  }

  // One-click "Mark Completed" for a file request: flip status to Approved,
  // notify the requesting agent, refresh the list.
  const handleCompleteFileRequest = async (deal) => {
    if (!deal?.createdat) return;
    try {
      await updateItem("deals", String(deal.createdat), {
        status: "Approved",
        approvedby: user.id,
      });
      try {
        await notifyAgentOnFileComplete(
          { ...deal, status: "Approved" },
          users,
          user,
        );
      } catch (notifyErr) {
        console.error("Failed to notify requesting agent:", notifyErr);
      }
      toast.success("File marked completed. Agent notified.");
      await refreshDeals();
    } catch (err) {
      console.error("Failed to mark completed:", err);
      toast.error("Could not mark this file completed.");
    }
  };

  const submitdeal = async (data, bool) => {
    const id = Date.now();
    setIsLoading(true);
    try {
      let imageUrls = [];
      if (data.step1.propertyphotofiles?.length > 0) {
        imageUrls = await uploadImagesToFirebase(data.step1.propertyphotofiles);
      }

      const selectedStage = data.step1.dealStage;
      const stageIndex = COLUMN_IDS.indexOf(selectedStage);
      const allowedStages = COLUMN_IDS.slice(0, stageIndex + 1);

      let allTasks = [];
      const taskStatusTracker = {};
      allowedStages.forEach((stage) => {
        const stageTasks = Array.isArray(tasks?.[stage]) ? tasks[stage] : [];
        allTasks = [...allTasks, ...stageTasks];
        taskStatusTracker[stage] = true;
      });

      const newdata = {
        status: bool ? "Approved" : "Pending",
        requestType: "saleTurnIn",
        propertyphoto: imageUrls,
        dealtype: data.step2.clientType,
        customers: data.step2.clientinfo,
        dealStage: data.step1.dealStage,
        financingType: data.step1.financingType,
        earnestMoney: data.step1.earnestMoney,
        offerPrice: data.step1.offerPrice,
        propertyType: data.step1.propertyType,
        agentCommission: data.step3.agentCommission,
        fixedagentcommission: data.step3.fixedagentcommission,
        agentData: data.step3.agentid,
        agentName: data.step3.agentName,
        brokeradminfee: data.step3.brokeradminfee,
        captype: data.step3.captype,
        captypedescription: data.step3.captypedescription,
        closingdate: data.step1.closingdate,
        inspection: data.step1.inspection,
        propertyLocation: data.step1.propertyLocation,
        inspectionDeadline: data.step1.inspectionDeadline,
        financingDeadline: data.step1.financingDeadline,
        leadCommission: data.step3.leadCommission,
        leadsource: data.step3.leadsource,
        propertyaddress: data.step1.propertyaddress,
        splitdescription: data.step3.splitdescription,
        splittype: data.step3.splittype,
        teamleadName: data.step3.teamleadName,
        flatfee: data.step4.flatfee,
        flatfeeamount: data.step4.flatfeeamount,
        assignedteamlead: data.step3.assignedteamlead,
        thirdpartyagentCommission: data.step3.thirdpartyagentCommission,
        thirdpartyagentName: data.step3.thirdpartyagentName,
        agentCommissionamount: data.step4.agentCommissionamount,
        bankcommission: data.step4.bankcommission,
        buyersidecommissionrate: data.step4.buyersidecommissionrate,
        leadsourceCommissionamount: data.step4.leadsourceCommissionamount,
        sellersidecommissionrate: data.step4.sellersidecommissionrate,
        takehomecommissionamount: data.step4.takehomecommissionamount,
        teamleadCommissionamount: data.step4.teamleadCommissionamount,
        teamleadcommissionrate: data.step4.teamleadcommissionrate,
        thirdpartyagentcommissionamount:
          data.step4.thirdpartyagentcommissionamount,
        totalCommissionamount: data.step4.totalCommissionamount,
        totalcommissionrate: data.step4.totalcommissionrate,
        tasks: allTasks,
        taskStatusTracker,
        createdby: user.id,
        createdat: id,
      };

      const result = await saveitemwithid("deals", id.toString(), newdata);
      if (result === "success") {
        allTasks.forEach((task) => {
          if (task?.assignee) {
            newmessagesuccess(
              "New Task Assigned",
              task.assignee?.id,
              task?.title || "",
              task.id || "",
              "newtask",
              newdata.createdat,
            );
          }
        });
        toast.success("Deal created successfully!");
        if (newdata.status === "Pending") {
          try {
            await notifyApprovers({ ...newdata, createdat: id }, users, user);
          } catch (notifyErr) {
            console.error("Failed to notify approvers:", notifyErr);
          }
        }
        await refreshDeals();
        setIsOpen(false);
      } else {
        toast.error(result || "Failed to create deal");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main>
      <PageHeader
        title="Dashboard"
        subtitle={`Welcome back, ${user?.firstname || ""}. Pending approvals are waiting for your review below.`}
      />

      <div className="px-3 lg:px-6 pt-3 lg:pt-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <ActionTile
            icon={ScrollText}
            label="Listing Request"
            gradient="from-emerald-500 to-teal-500"
            onClick={() => navigate("/dashboard/listing-request?type=seller")}
          />
          <ActionTile
            icon={Handshake}
            label="Buyer Request"
            gradient="from-blue-500 to-cyan-500"
            onClick={() => navigate("/dashboard/listing-request?type=buyer")}
          />
          <ActionTile
            icon={Send}
            label="Sale Turn In"
            gradient="from-amber-500 to-orange-500"
            onClick={() => setTurnInPickerOpen(true)}
          />
        </div>
      </div>

      <div className="px-3 lg:px-6 py-6 space-y-6">
        <PendingInboxPanel
          deals={deals}
          users={users}
          currentUser={user}
          onOpen={(deal) =>
            navigate(`/dashboard/deals?approve=${deal.createdat}`)
          }
          onCompleteFileRequest={handleCompleteFileRequest}
        />

        <FeedPanel />
      </div>

      {turnInPickerOpen && (
        <SaleTurnInSourcePicker
          onClose={() => setTurnInPickerOpen(false)}
          onSelect={(seed) => {
            setTurnInPickerOpen(false);
            setTurnInSeed(seed);
            setIsOpen(true);
          }}
        />
      )}

      {isOpen && (
        <AddNewDeal
          isLoading={isLoading}
          onClose={() => {
            setIsOpen(false);
            setTurnInSeed(null);
          }}
          onSubmit={(data, bool) => submitdeal(data, bool)}
          steps={steps}
          seed={turnInSeed}
        />
      )}

    </main>
  );
}

function ActionTile({ icon: Icon, label, gradient, onClick }) {
  return (
    <button
      onClick={onClick}
      className="group flex items-center gap-3 bg-white rounded-xl border border-gray-200 px-4 py-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-gray-300"
    >
      <div
        className={`w-9 h-9 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-105 transition-transform`}
      >
        <Icon size={16} className="text-white" />
      </div>
      <span className="font-semibold text-sm text-gray-800">{label}</span>
    </button>
  );
}

export default SupervisorDashboard;
